import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import {
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { FILTER_POSTAL_CODE, PAGE } from 'dpl/constants/query_params';
import { isMobileUA } from 'dpl/shared/utils';

import useSimilarBreedFilter from '../hooks/useSimilarBreedFilter';
import useSimilarBreedResults from '../hooks/useSimilarBreedResults';
import { RESULTS_TYPES } from '../utils/constants';
import resolveFeedPositions from '../utils/resolveFeedPositions';
import { sendResultGroupViewedEvent } from '../utils/resultGroupViewedEvent';
import { countNearbyBreeders } from '../utils/similarBreeds';
import FartherBreedersBanner from './FartherBreedersBanner';
import SearchAlertsBanner from './search_alerts/SearchAlertsBanner';
import SimilarBreedsModule from './similar_breeds/SimilarBreedsModule';
import BreederScreeningBanner from './BreederScreeningBanner';

const IS_MOBILE = isMobileUA();

// Its own rather than the mobile surfaces' shared four: this feed renders on
// desktop too, where a row of results is twice as wide.
const RESULT_GROUP_SIZE = IS_MOBILE ? 4 : 8;

// Desktop's puppy grid is four cards across, so its banners sit rows further
// down than the mobile anchors `resolveFeedPositions` owns.
const DESKTOP_PUPPIES_MINIMUM_RESULTS = 20;
const DESKTOP_PUPPIES_SCREENING_POSITION = 11;
const DESKTOP_PUPPIES_ALERTS_POSITION = 19;

// Alerts pull up to the last result on a short page, as they did before.
function desktopPuppiesFeedPositions(totalItemCount) {
  return {
    modulePosition: null,
    screeningPosition:
      totalItemCount >= DESKTOP_PUPPIES_MINIMUM_RESULTS
        ? DESKTOP_PUPPIES_SCREENING_POSITION
        : null,
    alertsPosition:
      totalItemCount > 0
        ? Math.min(DESKTOP_PUPPIES_ALERTS_POSITION, totalItemCount - 1)
        : null
  };
}

function SearchResultsItem({
  result,
  results,
  resultsType,
  position,
  positionOnPage,
  analyticsPageNumber,
  hideDistance,
  Component
}) {
  const breedId = useSelector(({ server }) => server.breedId);
  const queryClient = useQueryClient();

  const analyticsView =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
      : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;

  const analyticsViewFeature =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEW_FEATURES.PUPPY_CARD
      : ANALYTICS_VIEW_FEATURES.BREEDER_CARD;

  const analytics = useMemo(
    () => ({
      page_number: analyticsPageNumber,
      position,
      view: analyticsView,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
    }),
    [position, analyticsPageNumber, analyticsView]
  );

  const trackInView = useCallback(() => {
    if (positionOnPage % RESULT_GROUP_SIZE === 1) {
      const idx = results.indexOf(result);

      sendResultGroupViewedEvent(queryClient, {
        breedId,
        resultsType,
        resultGroup: results.slice(idx, idx + RESULT_GROUP_SIZE),
        position,
        page: analyticsPageNumber,
        view: analyticsView,
        viewFeature: analyticsViewFeature
      });
    }
  }, [
    results,
    positionOnPage,
    analyticsView,
    analyticsViewFeature,
    position,
    analyticsPageNumber,
    breedId,
    resultsType,
    queryClient
  ]);

  return (
    <Component
      result={result}
      analytics={analytics}
      hideDistance={hideDistance}
      trackInView={trackInView}
    />
  );
}

SearchResultsItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  results: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  result: PropTypes.object.isRequired,
  resultsType: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  positionOnPage: PropTypes.number.isRequired,
  analyticsPageNumber: PropTypes.number.isRequired,
  hideDistance: PropTypes.bool.isRequired,
  Component: PropTypes.func.isRequired
};

export default function SearchResults({
  results,
  resultsType,
  Card,
  distanceInMiles,
  totalItemCount,
  className,
  analyticsPageNumber
}) {
  const {
    itemsPerPage,
    stateLander,
    citySlug,
    preappliedFilters: { stateSlug } = {}
  } = useSelector(({ server }) => server);

  const params = useSelector(({ queryParams }) => queryParams);
  const currentPage = Number(params[PAGE]) || 1;
  const filteredByLocation = Boolean(params[FILTER_POSTAL_CODE] || stateSlug);

  const isFirstPage = currentPage === 1;

  // Mobile renders this feed for the puppy view only - `MobileLayout` routes
  // the breeder view to `CardlessSearchResults`, which mounts the module itself.
  const isMobilePuppiesView =
    IS_MOBILE && resultsType === RESULTS_TYPES.PUPPIES;
  const isDesktopPuppiesView =
    !IS_MOBILE && resultsType === RESULTS_TYPES.PUPPIES;

  // The exposed filter already mixes similar-breed puppies into this feed, so
  // the module defers to it whenever a visitor is actually seeing a mixed
  // feed - otherwise the same puppies could surface in both places.
  const {
    isMixed: isSimilarBreedsMixed,
    isMixPending: isSimilarBreedsMixPending
  } = useSimilarBreedFilter();

  const similarBreeds = useSimilarBreedResults({
    enabled:
      isMobilePuppiesView &&
      isFirstPage &&
      !isSimilarBreedsMixed &&
      !isSimilarBreedsMixPending
  });

  const nearbyBreederCount = useMemo(
    () => countNearbyBreeders(results),
    [results]
  );

  // The resolver's control arm reproduces the anchors this feed carried before
  // the experiment, so only the treatment arms move.
  const { modulePosition, screeningPosition, alertsPosition } =
    isDesktopPuppiesView
      ? desktopPuppiesFeedPositions(totalItemCount)
      : resolveFeedPositions({
          nearbyBreederCount,
          totalItemCount,
          variant: similarBreeds.variant
        });

  function getPosition(idx) {
    let position = idx + 1;
    if (currentPage > 1) {
      position += (currentPage - 1) * itemsPerPage;
    }

    return position;
  }

  return (
    <ul
      className={classnames('SearchResults SearchResults--v2 row', className)}
    >
      {results?.map((result, idx) => {
        const positionOnPage = idx + 1;

        const isFirstResult = idx === 0 && isFirstPage;
        const prevResult = results[idx - 1] || {};

        const resultStateSlug =
          resultsType === RESULTS_TYPES.PUPPIES
            ? result?.breeder_profile?.state_slug
            : result?.state_slug;

        const didStartInRange =
          isFirstResult || prevResult.distance_in_miles <= distanceInMiles;

        const isInRange = result.distance_in_miles <= distanceInMiles;
        const isInState = resultStateSlug === stateSlug;

        const didSwitchToOutOfRange =
          !stateLander && distanceInMiles && didStartInRange && !isInRange;

        const shouldDisplayBreederScreeningBanner =
          isFirstPage && idx === screeningPosition;

        const shouldDisplaySimilarBreedsModule =
          isMobilePuppiesView && isFirstPage && idx === modulePosition;

        // only hide if a distance limit is specified and it goes past that distance
        const shouldHideDistanceInPuppyCards = stateLander
          ? !isInState
          : !isInRange;

        const shouldDisplayFartherBreedersBanner =
          resultsType === RESULTS_TYPES.BREEDERS && filteredByLocation
            ? Boolean(
                !result.snoozed &&
                  !stateLander &&
                  !citySlug &&
                  distanceInMiles &&
                  didSwitchToOutOfRange
              )
            : false;

        const remainingResultsCount =
          totalItemCount - ((currentPage - 1) * itemsPerPage + idx);

        const shouldDisplaySearchAlertsBanner =
          isFirstPage && idx === alertsPosition;

        return (
          <Fragment key={result.id}>
            {shouldDisplayFartherBreedersBanner && (
              <li className="mb8 mb10-md col-12">
                <FartherBreedersBanner count={remainingResultsCount} />
              </li>
            )}
            <li
              className={classnames('mb10-md col-12 col-md-6 mb6 mb8-md', {
                'col-xl-3': resultsType === RESULTS_TYPES.PUPPIES
              })}
            >
              <SearchResultsItem
                result={result}
                resultsType={resultsType}
                results={results}
                position={getPosition(idx)}
                positionOnPage={positionOnPage}
                analyticsPageNumber={analyticsPageNumber}
                hideDistance={shouldHideDistanceInPuppyCards}
                Component={Card}
              />
            </li>
            {shouldDisplaySimilarBreedsModule && (
              <li className="mb6 mb8-md col-12">
                <SimilarBreedsModule
                  className="nh5"
                  variant={similarBreeds.variant}
                  resultsType={similarBreeds.resultsType}
                  view={ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS}
                  puppies={similarBreeds.puppies}
                  breeders={similarBreeds.breeders}
                  filters={similarBreeds.filters}
                  radiusInMiles={similarBreeds.radiusInMiles}
                  totalPuppyCount={similarBreeds.totalPuppyCount}
                />
              </li>
            )}
            {shouldDisplayBreederScreeningBanner && (
              <li className="mb6 mb8-md col-12">
                <BreederScreeningBanner />
              </li>
            )}
            {shouldDisplaySearchAlertsBanner && (
              <li className="mb12 col-12">
                <SearchAlertsBanner />
              </li>
            )}
          </Fragment>
        );
      })}
    </ul>
  );
}

SearchResults.propTypes = {
  className: PropTypes.string,
  results: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)).isRequired,
  Card: PropTypes.func.isRequired,
  distanceInMiles: PropTypes.number,
  totalItemCount: PropTypes.number,
  analyticsPageNumber: PropTypes.number.isRequired
};

SearchResults.defaultProps = {
  distanceInMiles: null,
  totalItemCount: 0,
  className: null
};
